import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import ReferenceList from '../../components/health/ReferenceList';
import ageBrackets from '../../data/health/ageBrackets';

function formatCitations(text) {
  return text
    .replace(/\[(\d+)\]/g, '<a href="#ref-$1" class="health-cite">[$1]</a>')
    .replace(/Cross-link: (\/health\/\S+)/g, '<a href="$1" style="color:var(--color-accent);font-size:0.8125rem">Read more &rarr;</a>');
}

const Section = ({ title, children }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
      transition={{ duration: 0.4 }}
      style={{ marginBottom: '2rem' }}
    >
      <h2 style={{ fontSize: '1.25rem' }}>{title}</h2>
      {children}
    </motion.div>
  );
};

const HealthAgeBracket = () => {
  const { slug } = useParams();
  const bracket = ageBrackets.find((b) => b.slug === slug);

  if (!bracket) {
    return <Navigate to="/health/by-age" replace />;
  }

  const currentIndex = ageBrackets.indexOf(bracket);
  const prev = currentIndex > 0 ? ageBrackets[currentIndex - 1] : null;
  const next = currentIndex < ageBrackets.length - 1 ? ageBrackets[currentIndex + 1] : null;

  return (
    <>
      <Helmet>
        <title>{bracket.title} | Health by Age</title>
        <meta name="description" content={bracket.summary} />
        <meta property="og:title" content={`${bracket.title} — Health by Age`} />
        <meta property="og:description" content={bracket.summary} />
      </Helmet>

      <div className="health-page health-content">
        {/* Breadcrumb */}
        <div style={{ marginBottom: '1.5rem', fontSize: '0.8125rem' }}>
          <Link to="/health" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>Health</Link>
          <span style={{ color: 'var(--color-text-muted)', margin: '0 0.5rem' }}>/</span>
          <Link to="/health/by-age" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>By Age</Link>
          <span style={{ color: 'var(--color-text-muted)', margin: '0 0.5rem' }}>/</span>
          <span style={{ color: 'var(--color-accent)' }}>{bracket.title}</span>
        </div>

        <h1 className="health-page-title">{bracket.title}</h1>
        <p className="text-gray-300" style={{ fontSize: '1.0625rem', marginBottom: '2rem' }}>
          {bracket.summary}
        </p>

        {/* Nutrition */}
        <Section title="Nutrition Priorities">
          <ul>
            {bracket.nutrition.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: formatCitations(item) }} />
            ))}
          </ul>
        </Section>

        {/* Movement */}
        <Section title="Movement & Fitness">
          <ul>
            {bracket.movement.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: formatCitations(item) }} />
            ))}
          </ul>
        </Section>

        {/* Avoid */}
        <Section title="What to Avoid / Watch Out For">
          <ul>
            {bracket.avoid.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: formatCitations(item) }} />
            ))}
          </ul>
        </Section>

        {/* Sleep */}
        <Section title="Sleep & Recovery">
          <p dangerouslySetInnerHTML={{ __html: formatCitations(bracket.sleep) }} />
        </Section>

        {/* Common mistakes */}
        <Section title="Common Mistakes at This Age">
          <ul>
            {bracket.mistakes.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: formatCitations(item) }} />
            ))}
          </ul>
        </Section>

        {/* Sets up for later */}
        <Section title="Sets Up for Later">
          <div className="health-callout">
            <div className="health-callout-text" dangerouslySetInnerHTML={{ __html: formatCitations(bracket.setsUpForLater) }} />
          </div>
        </Section>

        {/* References */}
        <ReferenceList references={bracket.references} />

        {/* Prev / Next navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border)' }}>
          {prev ? (
            <Link to={`/health/by-age/${prev.slug}`} style={{ color: 'var(--color-accent)', textDecoration: 'none', fontSize: '0.875rem' }}>
              &larr; {prev.title}
            </Link>
          ) : <span />}
          {next ? (
            <Link to={`/health/by-age/${next.slug}`} style={{ color: 'var(--color-accent)', textDecoration: 'none', fontSize: '0.875rem' }}>
              {next.title} &rarr;
            </Link>
          ) : <span />}
        </div>
      </div>
    </>
  );
};

export default HealthAgeBracket;
